/**
 * ROW 356, THE SHARED TABLE'S RULES (his words, 18 Sep 2026: "Allow in Customer care for the tables
 * for them to expand and also to adjust each of the columns and also to check/add/remove columns and
 * to sort by them and to sort by multiple and also to hav slicers", and "make the type of adjustments
 * like the ClariConnect portal - column in/out, resizing, etc."). Approval 169.
 *
 * Every Care table takes its behaviour from HERE, so one habit serves every screen and the
 * ClariConnect console alike: the same "Columns" tick list, the same 40 px floor on a dragged
 * heading, the same click-to-sort and Shift-click for a 2nd and 3rd key, the same reset wording.
 *
 * THE SAFETY RULE CARRIED FROM ROW 354: a slicer or a hidden column is a VIEW and changes nothing
 * recorded. Nothing is sliced by default; a table may name rows a slicer can never hide (the queue's
 * rows with no campaign, which a member started); and whatever the slicers do hide is COUNTED on
 * screen with one click back to everything. Sorting and slicing run over the whole list, never only
 * the page on screen (the paged screens send them to the server).
 *
 * Pure functions only; the React half is `components/grid/`.
 */

export interface SortKey {
  k: string;
  asc: boolean;
}

export type SortVal = string | number | null | undefined;

/** Selected values per slicer; a slicer with no values selected is not narrowing anything. */
export type SlicerSel = Record<string, string[]>;

export const MAX_SORT_KEYS = 3;
export const MIN_COL_WIDTH = 40;
export const MAX_COL_WIDTH = 2400;
/** The value a slicer uses for a row with nothing in that field (so "no campaign" is a choice too). */
export const BLANK = '__blank__';

/** The words, ClariConnect's where it has them, so one habit serves both screens. */
export const GRID_WORDING = {
  columns: 'Columns',
  columnsTip: "Tick the columns to show; drag a heading's right edge to resize",
  // His word, 16 Oct 2026: no Shift+click and no hint line; a click sorts, a second click flips.
  sortTip: 'Click to sort; click again to flip',
  reset: 'Show all, default widths',
  /** On a table that offers extra columns, "all" would be untrue: the extras go back off. */
  resetWithExtras: 'Default columns and widths',
  resetAll: 'Reset this table',
  resetTip: 'Every column shown at its default width, the first sort, and no filters',
  tooManyKeys: 'Three sort keys at most; click without Shift to start over.',
  alwaysShown: 'always shown',
  expand: 'Expand',
  expandTip: 'Show this table across the whole screen',
  collapse: 'Close full screen',
  collapseTip: 'Back to the page (Esc)',
  slicersAll: 'All',
  slicerSearch: (label: string) => `Search ${label.toLowerCase()}`,
  hidden: (n: number, noun: string) =>
    `${n.toLocaleString()} ${n === 1 ? noun.replace(/s$/, '') : noun} hidden by the filters above.`,
  protectedNote: (what: string) => `${what} always show.`,
  clear: 'Clear filters',
  noneLeft: 'No rows match these filters.',
} as const;

/**
 * A heading click, ClariConnect's rule exactly: a plain click makes this the only key (a click on the
 * only key flips it); Shift+click flips a key already in the list or adds it as the next key, up to
 * three. `firstAsc` is the direction a column starts in (dates start newest first on some tables).
 */
export function clickSort(
  keys: SortKey[],
  k: string,
  shift: boolean,
  firstAsc = true,
): { keys: SortKey[]; refused: boolean } {
  const i = keys.findIndex((s) => s.k === k);
  if (shift) {
    if (i >= 0) return { keys: keys.map((s, j) => (j === i ? { k: s.k, asc: !s.asc } : s)), refused: false };
    if (keys.length < MAX_SORT_KEYS) return { keys: [...keys, { k, asc: firstAsc }], refused: false };
    return { keys, refused: true };
  }
  if (i === 0 && keys.length === 1) return { keys: [{ k, asc: !keys[0]!.asc }], refused: false };
  return { keys: [{ k, asc: firstAsc }], refused: false };
}

function isBlank(v: SortVal): boolean {
  return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}

/** Numbers numerically, text without regard to case (and "9" before "10"). */
export function compareVals(a: SortVal, b: SortVal): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

/**
 * Sort by up to three keys. A blank sorts LAST in either direction (the Final Review rule), so
 * flipping a column never floods the top with empty cells. Ties keep the incoming order, so the
 * server's or the loader's own order survives underneath the operator's keys.
 */
export function multiSort<T>(rows: readonly T[], keys: readonly SortKey[], valueOf: (r: T, k: string) => SortVal): T[] {
  if (keys.length === 0) return rows.slice();
  const decorated = rows.map((r, i) => ({ r, i, v: keys.map((s) => valueOf(r, s.k)) }));
  decorated.sort((a, b) => {
    for (let j = 0; j < keys.length; j++) {
      const va = a.v[j];
      const vb = b.v[j];
      const ba = isBlank(va);
      const bb = isBlank(vb);
      if (ba && bb) continue;
      if (ba) return 1;
      if (bb) return -1;
      const c = compareVals(va, vb);
      if (c !== 0) return keys[j]!.asc ? c : -c;
    }
    return a.i - b.i;
  });
  return decorated.map((d) => d.r);
}

export interface SlicerDef<T> {
  key: string;
  label: string;
  /** One value, several (a row in two campaigns), or nothing (counts under BLANK). */
  value: (r: T) => string | readonly string[] | null | undefined;
  /** How a value reads on its button. */
  labelFor?: (v: string) => string;
  /** What a blank reads as ("No campaign"); without it a blank is not offered as a choice. */
  blankLabel?: string;
  /** Show a search box instead of buttons past this many values (default 12). */
  searchPast?: number;
}

function valuesOf<T>(def: SlicerDef<T>, r: T): string[] {
  const v = def.value(r);
  const arr = (Array.isArray(v) ? v : [v]) as Array<string | null | undefined>;
  const out = arr.filter((x): x is string => typeof x === 'string' && x.trim() !== '');
  return out.length ? out : [BLANK];
}

/** Only the slicers that are actually narrowing something. */
export function activeSlicers(sel: SlicerSel): SlicerSel {
  const out: SlicerSel = {};
  for (const [k, vs] of Object.entries(sel)) if (Array.isArray(vs) && vs.length) out[k] = vs;
  return out;
}

export function slicerCount(sel: SlicerSel): number {
  return Object.values(activeSlicers(sel)).reduce((n, vs) => n + vs.length, 0);
}

function passes<T>(r: T, defs: readonly SlicerDef<T>[], sel: SlicerSel, skip?: string): boolean {
  for (const d of defs) {
    if (d.key === skip) continue;
    const want = sel[d.key];
    if (!want || want.length === 0) continue;
    const have = valuesOf(d, r);
    if (!have.some((v) => want.includes(v))) return false;
  }
  return true;
}

/**
 * Apply the slicers: within a slicer the chosen values OR together, across slicers they AND. A row
 * `protect` names is never hidden (it is counted as shown). `hidden` is how many rows the slicers set
 * aside, which the screen must say.
 */
export function sliceRows<T>(
  rows: readonly T[],
  defs: readonly SlicerDef<T>[],
  sel: SlicerSel,
  protect?: (r: T) => boolean,
): { rows: T[]; hidden: number; keptByRule: number } {
  const act = activeSlicers(sel);
  if (Object.keys(act).length === 0) return { rows: rows.slice(), hidden: 0, keptByRule: 0 };
  const out: T[] = [];
  let hidden = 0;
  let keptByRule = 0;
  for (const r of rows) {
    if (passes(r, defs, act)) out.push(r);
    else if (protect && protect(r)) {
      out.push(r);
      keptByRule++;
    } else hidden++;
  }
  return { rows: out, hidden, keptByRule };
}

export interface Facet {
  v: string;
  n: number;
}

/**
 * The count beside each slicer button: rows that would show if this value were chosen, given every
 * OTHER slicer as it stands (so a button never reads 0 just because a sibling in its own slicer is
 * chosen). Values already chosen stay listed even at 0, so they can always be un-chosen.
 */
export function facetCounts<T>(rows: readonly T[], defs: readonly SlicerDef<T>[], sel: SlicerSel): Record<string, Facet[]> {
  const act = activeSlicers(sel);
  const out: Record<string, Facet[]> = {};
  for (const d of defs) {
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (!passes(r, defs, act, d.key)) continue;
      for (const v of valuesOf(d, r)) counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    if (!d.blankLabel) counts.delete(BLANK);
    for (const v of act[d.key] ?? []) if (!counts.has(v)) counts.set(v, 0);
    out[d.key] = [...counts.entries()]
      .map(([v, n]) => ({ v, n }))
      .sort((a, b) => (a.v === BLANK ? 1 : b.v === BLANK ? -1 : b.n - a.n || compareVals(a.v, b.v)));
  }
  return out;
}

export function toggleSlicer(sel: SlicerSel, key: string, v: string): SlicerSel {
  const cur = sel[key] ?? [];
  const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
  const out = { ...sel };
  if (next.length) out[key] = next;
  else delete out[key];
  return out;
}

/** What one table remembers for one operator in this browser. */
export interface GridPrefs {
  v: 1;
  hidden: string[];
  /** Columns that start hidden (an extra a table offers) which she has ticked on. */
  shown?: string[];
  widths: Record<string, number>;
  /** null = the table's own first sort. */
  sort: SortKey[] | null;
  slicers: SlicerSel;
}

export const EMPTY_PREFS: GridPrefs = { v: 1, hidden: [], widths: {}, sort: null, slicers: {} };

/** Per operator, per table (row 356 point 2). The operator is the signed-in address. */
export function prefsKey(table: string, operator: string): string {
  const who = (operator || '').trim().toLowerCase() || 'anyone';
  return `membercare.grid.${table}.${who}`;
}

export function clampWidth(w: number): number {
  return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.round(w)));
}

export interface PrefsShape {
  /** Every column key the table has. */
  columns: readonly string[];
  /** Columns that can never be hidden (a tick box, the row's own action). */
  locked?: readonly string[];
  /** Columns that start hidden: extras a table offers in its Columns list. */
  offByDefault?: readonly string[];
  /** Columns a sort may name. */
  sortable: readonly string[];
  /** Slicer keys the table has. */
  slicers: readonly string[];
}

/**
 * Read back what was stored, keeping only what this table still has: a column renamed or removed in
 * a later build, a locked column somehow marked hidden, a width off the scale, a fourth sort key, or
 * a slicer that no longer exists are all DROPPED rather than trusted. Anything unreadable is the
 * empty layout, never an error.
 */
export function sanitizePrefs(raw: unknown, shape: PrefsShape): GridPrefs {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_PREFS };
  const o = raw as Record<string, unknown>;
  const cols = new Set(shape.columns);
  const locked = new Set(shape.locked ?? []);
  const sortable = new Set(shape.sortable);
  const slicerKeys = new Set(shape.slicers);
  const hidden = Array.isArray(o.hidden)
    ? [...new Set(o.hidden.filter((k): k is string => typeof k === 'string' && cols.has(k) && !locked.has(k)))]
    : [];
  const extras = new Set(shape.offByDefault ?? []);
  const shown = Array.isArray(o.shown)
    ? [...new Set(o.shown.filter((k): k is string => typeof k === 'string' && extras.has(k)))]
    : [];
  const widths: Record<string, number> = {};
  if (o.widths && typeof o.widths === 'object') {
    for (const [k, w] of Object.entries(o.widths as Record<string, unknown>)) {
      if (cols.has(k) && typeof w === 'number' && Number.isFinite(w)) widths[k] = clampWidth(w);
    }
  }
  let sort: SortKey[] | null = null;
  if (Array.isArray(o.sort)) {
    const seen = new Set<string>();
    const ks: SortKey[] = [];
    for (const s of o.sort) {
      if (!s || typeof s !== 'object') continue;
      const { k, asc } = s as Record<string, unknown>;
      if (typeof k !== 'string' || !sortable.has(k) || seen.has(k) || typeof asc !== 'boolean') continue;
      seen.add(k);
      ks.push({ k, asc });
      if (ks.length === MAX_SORT_KEYS) break;
    }
    sort = ks.length ? ks : null;
  }
  const slicers: SlicerSel = {};
  if (o.slicers && typeof o.slicers === 'object') {
    for (const [k, vs] of Object.entries(o.slicers as Record<string, unknown>)) {
      if (!slicerKeys.has(k) || !Array.isArray(vs)) continue;
      const clean = [...new Set(vs.filter((v): v is string => typeof v === 'string' && v.length > 0 && v.length <= 200))].slice(0, 50);
      if (clean.length) slicers[k] = clean;
    }
  }
  return { v: 1, hidden, ...(shown.length ? { shown } : null), widths, sort, slicers };
}

export function readPrefs(key: string, shape: PrefsShape): GridPrefs {
  try {
    const s = localStorage.getItem(key);
    return s ? sanitizePrefs(JSON.parse(s), shape) : { ...EMPTY_PREFS };
  } catch {
    return { ...EMPTY_PREFS };
  }
}

export function writePrefs(key: string, prefs: GridPrefs): void {
  try {
    const empty =
      prefs.hidden.length === 0 && !(prefs.shown ?? []).length && Object.keys(prefs.widths).length === 0 && !prefs.sort
      && slicerCount(prefs.slicers) === 0;
    if (empty) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    /* storage refused (private window, quota): the layout simply is not remembered */
  }
}

/** The wire form of a sort for the paged screens' servers: `k1:asc,k2:desc`. */
export function sortParam(keys: readonly SortKey[]): string {
  return keys.map((s) => `${s.k}:${s.asc ? 'asc' : 'desc'}`).join(',');
}

/** The wire form of the slicers: `key=v1|v2;key2=v3`, values URI-encoded. */
export function slicerParam(sel: SlicerSel): string {
  return Object.entries(activeSlicers(sel))
    .map(([k, vs]) => `${k}=${vs.map((v) => encodeURIComponent(v)).join('|')}`)
    .join(';');
}
